import { useEffect, useRef, useState } from "react";
import { Dialog } from "./ui";
import { t } from "../lib/sprache";

/** Ein erzeugtes oder gespeichertes PDF für die Vorschau */
export interface PdfDatei { blob: Blob; filename: string }

/** Inhalt der Vorschau: Titel und Weg zum PDF (erst beim Öffnen erzeugt bzw. geladen) */
export interface PdfQuelle { title: string; laden: () => Promise<PdfDatei> }

/**
 * Gemeinsame Vorschau für Belege (K-538, nach dem Muster der Pitch-Liste und der Vertragsvorschau): Angebot, Rechnung,
 * Gutschrift, Storno und Mahnschreiben öffnen „Vorschau“ als Fenster mit dem PDF, dazu „PDF herunterladen“, „Drucken“ und
 * „In neuem Tab öffnen“. Herunterladen bleibt ein eigener Knopf neben der Vorschau.
 */
export function PdfVorschau({ quelle, onClose }: { quelle: PdfQuelle | null; onClose: () => void }) {
  const [datei, setDatei] = useState<PdfDatei | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (!quelle) return;
    let alive = true;
    let current: string | null = null;
    setDatei(null); setUrl(null); setFehler(null);
    quelle.laden().then((d) => {
      if (!alive) return;
      const pdf = d.blob.type === "application/pdf" ? d.blob : new Blob([d.blob], { type: "application/pdf" });
      current = URL.createObjectURL(pdf);
      setDatei({ blob: pdf, filename: d.filename });
      setUrl(current);
    }).catch((e: unknown) => { if (alive) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; if (current) URL.revokeObjectURL(current); };
  }, [quelle]);

  const herunterladen = () => {
    if (!datei || !url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = datei.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const drucken = () => {
    try {
      frame.current?.contentWindow?.focus();
      frame.current?.contentWindow?.print();
    } catch {
      if (url) window.open(url, "_blank", "noopener");
    }
  };

  return (
    <Dialog open={!!quelle} onClose={onClose} title={quelle?.title ?? t("Vorschau", "Preview")} wide>
      <div className="stack tk-pdf-vorschau">
        <div className="row tk-pdf-vorschau-aktionen">
          <button type="button" className="primary" disabled={!datei} onClick={herunterladen}>{t("PDF herunterladen", "Download PDF")}</button>
          <button type="button" disabled={!datei} onClick={drucken}>{t("Drucken", "Print")}</button>
          {url && <a className="btn" href={url} target="_blank" rel="noopener">{t("In neuem Tab öffnen", "Open in new tab")}</a>}
        </div>
        {fehler && <p className="tk-error" role="alert">{t("Die Vorschau ließ sich nicht erzeugen: ", "The preview could not be created: ")}{fehler}</p>}
        {!url && !fehler && <p className="muted" role="status">{t("Vorschau wird erstellt …", "Creating preview …")}</p>}
        {url && <iframe ref={frame} className="tk-pdf-vorschau-rahmen" src={`${url}#view=FitH`} title={quelle?.title ?? t("Vorschau", "Preview")} />}
        <small className="muted tk-pdf-vorschau-handy">{t("Auf dem Handy zeigt der Browser das PDF oft nur verkleinert; „In neuem Tab öffnen“ zeigt alle Seiten.", "On phones the browser often shows the PDF only scaled down; “Open in new tab” shows all pages.")}</small>
      </div>
    </Dialog>
  );
}
